/*!

=========================================================
* Black Dashboard React v1.2.2
=========================================================

* Product Page: https://www.creative-tim.com/product/black-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/black-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from "react";
// nodejs library that concatenates classes
import classNames from "classnames";
import { useNavigate } from "react-router-dom";

// reactstrap components
import {
  Button,
  Collapse,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  Input,
  InputGroup,
  NavbarBrand,
  Navbar,
  NavLink,
  Nav,
  Container,
  Modal,
  NavbarToggler,
  ModalHeader,
  ModalBody,
  Spinner,
} from "reactstrap";

import { useAuth } from "../../contexts/AuthContext";
import { searchService } from "../../services/searchService";
import { empresasService } from "../../services/empresasService";
import { usePermissions } from "../../hooks/usePermissions";

// Importar el manual de usuario
const manualUsuario = require("../../assets/document/MANUAL_USUARIO_SGCM.pdf");

function AdminNavbar(props) {
  const [collapseOpen, setcollapseOpen] = React.useState(false);
  const [modalSearch, setmodalSearch] = React.useState(false);
  const [color, setcolor] = React.useState("navbar-transparent");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [searchResults, setSearchResults] = React.useState(null);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(-1);
  const searchInputRef = React.useRef(null);
  const { user, logout } = useAuth();
  const { canView } = usePermissions();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };
  React.useEffect(() => {
    window.addEventListener("resize", updateColor);
    // Specify how to clean up after this effect:
    return function cleanup() {
      window.removeEventListener("resize", updateColor);
    };
  });

  // Atajo de teclado Ctrl + B para abrir búsqueda
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      // Evitar el atajo si el usuario está escribiendo en un input o textarea
      const isInputFocused = document.activeElement?.tagName === 'INPUT' ||
        document.activeElement?.tagName === 'TEXTAREA';

      if ((e.ctrlKey || e.metaKey) && e.key === 'b' && !isInputFocused) {
        e.preventDefault();
        if (!modalSearch) {
          setmodalSearch(true);
        }
      }
      // ESC para cerrar
      if (e.key === 'Escape' && modalSearch) {
        setmodalSearch(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [modalSearch]);

  // Focus en el input cuando se abre el modal
  React.useEffect(() => {
    if (modalSearch) {
      // Limpiar búsqueda anterior
      setSearchQuery("");
      setSearchResults(null);
      setSelectedIndex(-1);

      // Hacer focus después de que el modal esté completamente renderizado
      // Reactstrap puede necesitar más tiempo para renderizar el input
      const focusInput = () => {
        if (searchInputRef.current) {
          // Intentar acceder al input real (puede estar dentro de un wrapper)
          const inputElement = searchInputRef.current.querySelector?.('input') ||
            searchInputRef.current.input ||
            searchInputRef.current;

          if (inputElement && typeof inputElement.focus === 'function') {
            inputElement.focus();
            // Verificar que realmente tiene el focus
            setTimeout(() => {
              if (document.activeElement !== inputElement && document.activeElement !== searchInputRef.current) {
                // Último intento con el elemento directamente
                if (searchInputRef.current.focus) {
                  searchInputRef.current.focus();
                }
              }
            }, 50);
          }
        }
      };

      // Intentar focus múltiples veces para asegurar que funcione
      const timeout1 = setTimeout(focusInput, 200);
      const timeout2 = setTimeout(focusInput, 400);

      return () => {
        clearTimeout(timeout1);
        clearTimeout(timeout2);
      };
    }
  }, [modalSearch]);

  // Usar useRef para mantener una referencia estable a canView
  const canViewRef = React.useRef(canView);
  React.useEffect(() => {
    canViewRef.current = canView;
  }, [canView]);

  // Búsqueda en tiempo real
  React.useEffect(() => {
    if (!modalSearch) return;

    const timeoutId = setTimeout(async () => {
      if (searchQuery.trim().length < 2) {
        setSearchResults(null);
        setSearchLoading(false);
        return;
      }

      setSearchLoading(true);
      // No limpiar resultados anteriores para evitar parpadeo
      // Los resultados se actualizarán cuando la búsqueda termine
      try {
        // Obtener empresa activa
        let empresaId = user?.EmpresaID || user?.empresaID || null;
        if (!empresaId) {
          const empresasResponse = await empresasService.getAll();
          if (empresasResponse.success && empresasResponse.data) {
            const empresasData = Array.isArray(empresasResponse.data) ? empresasResponse.data : [];
            const empresaActiva = empresasData.find(emp => {
              const activo = emp.Activo !== undefined ? emp.Activo : (emp.activo !== undefined ? emp.activo : true);
              return activo === true || activo === 1;
            }) || empresasData[0];
            if (empresaActiva) {
              empresaId = empresaActiva.EmpresaID || empresaActiva.empresaID;
            }
          }
        }

        const sucursalId = user?.SucursalID || user?.sucursalID || null;
        // Usar la referencia estable en lugar de canView directamente
        const results = await searchService.search(searchQuery, empresaId, sucursalId, canViewRef.current);
        if (results.success) {
          setSearchResults(results.data);
        } else {
          // Si hay error, establecer resultados vacíos
          setSearchResults({
            modulos: [],
            empleados: [],
            productos: [],
            clientes: [],
            ventas: [],
            categorias: [],
            sucursales: [],
          });
        }
      } catch (error) {
        // En caso de error, establecer resultados vacíos
        setSearchResults({
          modulos: [],
          empleados: [],
          productos: [],
          clientes: [],
          ventas: [],
          categorias: [],
          sucursales: [],
        });
      } finally {
        setSearchLoading(false);
      }
    }, 300); // Debounce de 300ms

    return () => clearTimeout(timeoutId);
  }, [searchQuery, modalSearch, user]); // Removido canView de las dependencias
  // function that adds color white/transparent to the navbar on resize (this is for the collapse)
  const updateColor = () => {
    if (window.innerWidth < 993 && collapseOpen) {
      setcolor("bg-white");
    } else {
      setcolor("navbar-transparent");
    }
  };
  // this function opens and closes the collapse on small devices
  const toggleCollapse = () => {
    if (collapseOpen) {
      setcolor("navbar-transparent");
    } else {
      setcolor("bg-white");
    }
    setcollapseOpen(!collapseOpen);
  };
  // this function is to open the Search modal
  const toggleModalSearch = () => {
    setmodalSearch(!modalSearch);
  };

  // Navegar a un resultado
  const handleResultClick = (result) => {
    if (result.path) {
      // Si es un elemento específico (no un módulo), guardar el ID para abrir el detalle
      if (result.type && result.id) {
        // Guardar en sessionStorage para que la vista lo detecte y abra el detalle
        sessionStorage.setItem(`openDetail_${result.type}`, result.id.toString());
      }
      
      // Cerrar el modal primero para evitar conflictos con la navegación
      setmodalSearch(false);
      
      // Limpiar el estado de búsqueda
      setSearchQuery("");
      setSearchResults(null);
      setSelectedIndex(-1);
      
      // Navegar después de un pequeño delay para asegurar que el modal se cierre completamente
      setTimeout(() => {
        navigate(result.path);
      }, 100);
    }
  };

  // Manejar navegación con teclado
  const handleKeyDown = (e) => {
    if (!searchResults) return;

    const allResults = [
      ...searchResults.modulos,
      ...searchResults.empleados,
      ...searchResults.productos,
      ...searchResults.clientes,
      ...searchResults.cotizaciones,
      ...searchResults.ventas,
      ...searchResults.facturas,
      ...searchResults.categorias,
      ...searchResults.sucursales,
    ];

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev < allResults.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0 && allResults[selectedIndex]) {
      e.preventDefault();
      handleResultClick(allResults[selectedIndex]);
    }
  };

  // Función para descargar el manual de usuario
  const handleDownloadManual = () => {
    const link = document.createElement('a');
    link.href = manualUsuario;
    link.download = 'MANUAL_USUARIO_SGCM.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  return (
    <>
      <Navbar className={classNames("navbar-absolute", color)} expand="lg">
        <Container fluid>
          <div className="navbar-wrapper">
            <div
              className={classNames("navbar-toggle d-inline", {
                toggled: props.sidebarOpened,
              })}
            >
              <NavbarToggler onClick={props.toggleSidebar}>
                <span className="navbar-toggler-bar bar1" />
                <span className="navbar-toggler-bar bar2" />
                <span className="navbar-toggler-bar bar3" />
              </NavbarToggler>
            </div>
            <NavbarBrand href="#pablo" onClick={(e) => e.preventDefault()}>
              {props.brandText}
            </NavbarBrand>
          </div>
          <NavbarToggler onClick={toggleCollapse}>
            <span className="navbar-toggler-bar navbar-kebab" />
            <span className="navbar-toggler-bar navbar-kebab" />
            <span className="navbar-toggler-bar navbar-kebab" />
          </NavbarToggler>
          <Collapse navbar isOpen={collapseOpen}>
            <Nav className="ml-auto" navbar>
              <InputGroup className="search-bar">
                <Button color="link" onClick={toggleModalSearch}>
                  <i className="tim-icons icon-zoom-split" />
                  <span className="d-lg-none d-md-block">Search</span>
                </Button>
              </InputGroup>
              <InputGroup className="manual-download">
                <Button
                  color="link"
                  onClick={handleDownloadManual}
                  title="Descargar Manual de Usuario"
                >
                  <i className="tim-icons icon-book-bookmark" />
                  <span className="d-lg-none d-md-block">Descargar manual</span>
                </Button>
              </InputGroup>
              {/* Sección de notificaciones - Comentada por ahora, disponible para funcionalidad premium */}
              {/* <UncontrolledDropdown nav>
                <DropdownToggle
                  caret
                  color="default"
                  data-toggle="dropdown"
                  nav
                >
                  <div className="notification d-none d-lg-block d-xl-block" />
                  <i className="tim-icons icon-sound-wave" />
                  <p className="d-lg-none">Notifications</p>
                </DropdownToggle>
                <DropdownMenu className="dropdown-navbar" right tag="ul">
                  <NavLink tag="li">
                    <DropdownItem className="nav-item">
                      Mike John responded to your email
                    </DropdownItem>
                  </NavLink>
                  <NavLink tag="li">
                    <DropdownItem className="nav-item">
                      You have 5 more tasks
                    </DropdownItem>
                  </NavLink>
                  <NavLink tag="li">
                    <DropdownItem className="nav-item">
                      Your friend Michael is in town
                    </DropdownItem>
                  </NavLink>
                  <NavLink tag="li">
                    <DropdownItem className="nav-item">
                      Another notification
                    </DropdownItem>
                  </NavLink>
                  <NavLink tag="li">
                    <DropdownItem className="nav-item">
                      Another one
                    </DropdownItem>
                  </NavLink>
                </DropdownMenu>
              </UncontrolledDropdown> */}
              <UncontrolledDropdown nav>
                <DropdownToggle
                  caret
                  color="default"
                  nav
                  onClick={(e) => e.preventDefault()}
                >
                  <div className="photo">
                    <img alt="..." src={require("assets/img/anime3.png")} />
                  </div>
                  <b className="caret d-none d-lg-block d-xl-block" />
                  <p className="d-lg-none">{user?.nombre || "Usuario"}</p>
                </DropdownToggle>
                <DropdownMenu className="dropdown-navbar" right tag="ul">
                  <NavLink tag="li">
                    <DropdownItem className="nav-item">
                      {user?.nombre || "Usuario"}
                    </DropdownItem>
                  </NavLink>
                  <NavLink tag="li">
                    <DropdownItem className="nav-item">
                      {user?.correo || ""}
                    </DropdownItem>
                  </NavLink>
                  <DropdownItem divider tag="li" />
                  <NavLink tag="li">
                    <DropdownItem className="nav-item" onClick={handleLogout}>
                      Cerrar Sesión
                    </DropdownItem>
                  </NavLink>
                </DropdownMenu>
              </UncontrolledDropdown>
              <li className="separator d-lg-none" />
            </Nav>
          </Collapse>
        </Container>
      </Navbar>
      <Modal
        modalClassName="modal-search"
        isOpen={modalSearch}
        toggle={toggleModalSearch}
        size="lg"
        onOpened={() => {
          // Asegurar focus cuando el modal esté completamente abierto
          setTimeout(() => {
            if (searchInputRef.current) {
              const inputElement = searchInputRef.current.querySelector?.('input') ||
                searchInputRef.current.input ||
                searchInputRef.current;
              if (inputElement && typeof inputElement.focus === 'function') {
                inputElement.focus();
              }
            }
          }, 100);
        }}
      >
        <ModalHeader>
          <Input
            innerRef={searchInputRef}
            placeholder="Buscar en el sistema... (Ctrl+B)"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus={false}
            style={{ outline: 'none' }}
          />
          <button
            aria-label="Close"
            className="close"
            onClick={toggleModalSearch}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
        </ModalHeader>
        <ModalBody style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {searchLoading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
              <p className="mt-2">Buscando...</p>
            </div>
          ) : searchQuery.trim().length < 2 ? (
            <div className="text-center py-4 text-muted">
              <i className="tim-icons icon-zoom-split" style={{ fontSize: '3rem', opacity: 0.3 }} />
              <p className="mt-3">Escribe al menos 2 caracteres para buscar</p>
              <small>Presiona Ctrl+B para abrir este buscador</small>
            </div>
          ) : searchResults ? (
            <>
              {searchResults.modulos.length === 0 &&
                searchResults.empleados.length === 0 &&
                searchResults.productos.length === 0 &&
                searchResults.clientes.length === 0 &&
                searchResults.cotizaciones.length === 0 &&
                searchResults.ventas.length === 0 &&
                searchResults.facturas.length === 0 &&
                searchResults.categorias.length === 0 &&
                searchResults.sucursales.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <p>No se encontraron resultados</p>
                </div>
              ) : null}

              {searchResults.modulos.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-muted mb-2">
                    <i className="tim-icons icon-settings-gear-63 mr-2" />
                    Módulos
                  </h6>
                  {searchResults.modulos.map((modulo, idx) => {
                    const globalIdx = idx;
                    return (
                      <div
                        key={modulo.path}
                        className={`search-result-item ${selectedIndex === globalIdx ? 'selected' : ''}`}
                        onClick={() => handleResultClick(modulo)}
                        style={{
                          padding: '0.75rem',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          marginBottom: '0.25rem',
                          backgroundColor: selectedIndex === globalIdx ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <i className={`${modulo.icon} mr-3`} />
                          <div>
                            <div className="font-weight-bold">{modulo.name}</div>
                            <small className="text-muted">{modulo.category}</small>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {searchResults.empleados && searchResults.empleados.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-muted mb-2">
                    <i className="tim-icons icon-badge mr-2" />
                    Empleados
                  </h6>
                  {searchResults.empleados.map((empleado, idx) => {
                    const globalIdx = searchResults.modulos.length + idx;
                    return (
                      <div
                        key={`empleado-${empleado.id}`}
                        className={`search-result-item ${selectedIndex === globalIdx ? 'selected' : ''}`}
                        onClick={() => handleResultClick(empleado)}
                        style={{
                          padding: '0.75rem',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          marginBottom: '0.25rem',
                          backgroundColor: selectedIndex === globalIdx ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <i className="tim-icons icon-badge mr-3" />
                          <div>
                            <div className="font-weight-bold">{empleado.name}</div>
                            {empleado.numero && <small className="text-muted mr-3"># Ext: {empleado.numero}</small>}
                            {empleado.curp && <small className="text-muted">CURP: {empleado.curp}</small>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {searchResults.productos.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-muted mb-2">
                    <i className="tim-icons icon-box-2 mr-2" />
                    Productos
                  </h6>
                  {searchResults.productos.map((producto, idx) => {
                    const globalIdx = searchResults.modulos.length + searchResults.empleados.length + idx;
                    return (
                      <div
                        key={`producto-${producto.id}`}
                        className={`search-result-item ${selectedIndex === globalIdx ? 'selected' : ''}`}
                        onClick={() => handleResultClick(producto)}
                        style={{
                          padding: '0.75rem',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          marginBottom: '0.25rem',
                          backgroundColor: selectedIndex === globalIdx ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between w-100">
                          <div className="d-flex align-items-center">
                            <i className="tim-icons icon-box-2 mr-3" />
                            <div>
                              <div className="font-weight-bold">{producto.name}</div>
                              <small className="text-muted">SKU: {producto.sku}</small>
                            </div>
                          </div>
                          {producto.stockActual !== null && producto.stockActual !== undefined && (
                            <div className="text-right">
                              <div className="font-weight-bold" style={{ fontSize: '0.9rem' }}>
                                Stock: {parseFloat(producto.stockActual).toFixed(2)}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {searchResults.clientes.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-muted mb-2">
                    <i className="tim-icons icon-single-02 mr-2" />
                    Clientes
                  </h6>
                  {searchResults.clientes.map((cliente, idx) => {
                    const globalIdx = searchResults.modulos.length + searchResults.empleados.length + searchResults.productos.length + idx;
                    return (
                      <div
                        key={`cliente-${cliente.id}`}
                        className={`search-result-item ${selectedIndex === globalIdx ? 'selected' : ''}`}
                        onClick={() => handleResultClick(cliente)}
                        style={{
                          padding: '0.75rem',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          marginBottom: '0.25rem',
                          backgroundColor: selectedIndex === globalIdx ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <i className="tim-icons icon-single-02 mr-3" />
                          <div>
                            <div className="font-weight-bold">{cliente.name}</div>
                            {cliente.rfc && <small className="text-muted">RFC: {cliente.rfc}</small>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {searchResults.cotizaciones.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-muted mb-2">
                    <i className="tim-icons icon-notes mr-2" />
                    Cotizaciones
                  </h6>
                  {searchResults.cotizaciones.map((cotizacion, idx) => {
                    const globalIdx = searchResults.modulos.length + searchResults.empleados.length + searchResults.productos.length + searchResults.clientes.length + idx;
                    return (
                      <div
                        key={`cotizacion-${cotizacion.id}`}
                        className={`search-result-item ${selectedIndex === globalIdx ? 'selected' : ''}`}
                        onClick={() => handleResultClick(cotizacion)}
                        style={{
                          padding: '0.75rem',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          marginBottom: '0.25rem',
                          backgroundColor: selectedIndex === globalIdx ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <i className="tim-icons icon-notes mr-3" />
                            <div>
                              <div className="font-weight-bold">{cotizacion.name}</div>
                              {cotizacion.cliente && <small className="text-muted">Cliente: {cotizacion.cliente}</small>}
                            </div>
                          </div>
                          {cotizacion.total && (
                            <div className="text-right">
                              <div className="font-weight-bold">${parseFloat(cotizacion.total).toFixed(2)}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {searchResults.ventas.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-muted mb-2">
                    <i className="tim-icons icon-cart mr-2" />
                    Ventas
                  </h6>
                  {searchResults.ventas.map((venta, idx) => {
                    const globalIdx = searchResults.modulos.length + searchResults.empleados.length + searchResults.productos.length + searchResults.clientes.length + searchResults.cotizaciones.length + idx;
                    return (
                      <div
                        key={`venta-${venta.id}`}
                        className={`search-result-item ${selectedIndex === globalIdx ? 'selected' : ''}`}
                        onClick={() => handleResultClick(venta)}
                        style={{
                          padding: '0.75rem',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          marginBottom: '0.25rem',
                          backgroundColor: selectedIndex === globalIdx ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <i className="tim-icons icon-cart mr-3" />
                            <div>
                              <div className="font-weight-bold">{venta.name}</div>
                              {venta.cliente && <small className="text-muted">Cliente: {venta.cliente}</small>}
                            </div>
                          </div>
                          {venta.total && (
                            <div className="text-right">
                              <div className="font-weight-bold">${parseFloat(venta.total).toFixed(2)}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {searchResults.facturas.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-muted mb-2">
                    <i className="tim-icons icon-paper mr-2" />
                    Facturas
                  </h6>
                  {searchResults.facturas.map((factura, idx) => {
                    const globalIdx = searchResults.modulos.length + searchResults.empleados.length + searchResults.productos.length + searchResults.clientes.length + searchResults.cotizaciones.length + searchResults.ventas.length + idx;
                    return (
                      <div
                        key={`factura-${factura.id}`}
                        className={`search-result-item ${selectedIndex === globalIdx ? 'selected' : ''}`}
                        onClick={() => handleResultClick(factura)}
                        style={{
                          padding: '0.75rem',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          marginBottom: '0.25rem',
                          backgroundColor: selectedIndex === globalIdx ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <i className="tim-icons icon-paper mr-3" />
                            <div>
                              <div className="font-weight-bold">{factura.name}</div>
                              {factura.cliente && <small className="text-muted">Cliente: {factura.cliente}</small>}
                              {factura.uuid && <small className="text-muted d-block">UUID: {factura.uuid.substring(0, 8)}...</small>}
                              {factura.estatus && (
                                <small className={`d-block ${factura.estatus.toLowerCase() === 'timbrada' ? 'text-success' : factura.estatus.toLowerCase() === 'cancelada' ? 'text-danger' : 'text-warning'}`}>
                                  {factura.estatus}
                                </small>
                              )}
                            </div>
                          </div>
                          {factura.total && (
                            <div className="text-right">
                              <div className="font-weight-bold">${parseFloat(factura.total).toFixed(2)}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {searchResults.categorias.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-muted mb-2">
                    <i className="tim-icons icon-tag mr-2" />
                    Categorías
                  </h6>
                  {searchResults.categorias.map((categoria, idx) => {
                    const globalIdx = searchResults.modulos.length + searchResults.empleados.length + searchResults.productos.length + searchResults.clientes.length + searchResults.cotizaciones.length + searchResults.ventas.length + searchResults.facturas.length + idx;
                    return (
                      <div
                        key={`categoria-${categoria.id}`}
                        className={`search-result-item ${selectedIndex === globalIdx ? 'selected' : ''}`}
                        onClick={() => handleResultClick(categoria)}
                        style={{
                          padding: '0.75rem',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          marginBottom: '0.25rem',
                          backgroundColor: selectedIndex === globalIdx ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <i className="tim-icons icon-tag mr-3" />
                          <div className="font-weight-bold">{categoria.name}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {searchResults.sucursales.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-muted mb-2">
                    <i className="tim-icons icon-shop mr-2" />
                    Sucursales
                  </h6>
                  {searchResults.sucursales.map((sucursal, idx) => {
                    const globalIdx = searchResults.modulos.length + searchResults.empleados.length + searchResults.productos.length + searchResults.clientes.length + searchResults.cotizaciones.length + searchResults.ventas.length + searchResults.facturas.length + searchResults.categorias.length + idx;
                    return (
                      <div
                        key={`sucursal-${sucursal.id}`}
                        className={`search-result-item ${selectedIndex === globalIdx ? 'selected' : ''}`}
                        onClick={() => handleResultClick(sucursal)}
                        style={{
                          padding: '0.75rem',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          marginBottom: '0.25rem',
                          backgroundColor: selectedIndex === globalIdx ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <i className="tim-icons icon-shop mr-3" />
                          <div className="font-weight-bold">{sucursal.name}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : null}
        </ModalBody>
      </Modal>
    </>
  );
}

export default AdminNavbar;
